import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility, VisibilityOff, School,
  Email as EmailIcon, Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f1923 0%, #1a2332 30%, #1a9e8f 70%, #0d7377 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated floating orbs */}
      <Box sx={{
        position: 'absolute', top: '-10%', right: '-5%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(26,158,143,0.25) 0%, transparent 70%)',
        animation: 'float 8s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0) scale(1)' },
          '50%': { transform: 'translateY(-30px) scale(1.05)' },
        }
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-15%', left: '-8%',
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
        animation: 'float2 10s ease-in-out infinite',
        '@keyframes float2': {
          '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
          '50%': { transform: 'translateY(20px) rotate(10deg)' },
        }
      }} />
      <Box sx={{
        position: 'absolute', top: '50%', left: '20%',
        width: 200, height: 200, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(26,158,143,0.1) 0%, transparent 70%)',
        animation: 'float 12s ease-in-out infinite reverse',
      }} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          sx={{
            borderRadius: '20px !important',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 40px rgba(26,158,143,0.1) !important',
            border: 'none !important',
            overflow: 'hidden',
            backdropFilter: 'blur(20px)',
            background: 'rgba(255,255,255,0.97) !important',
          }}
        >
          {/* Premium header with gradient */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a2332 0%, #1a9e8f 100%)',
              py: 4,
              px: 3,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -40, right: -40,
                width: 120, height: 120,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -20, left: -20,
                width: 80, height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <Box sx={{
              width: 64, height: 64, borderRadius: '16px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}>
              <School sx={{ fontSize: 32, color: '#fff' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: '#fff !important',
                fontWeight: 800,
                letterSpacing: -0.5,
              }}
            >
              VP School MIS
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.7) !important',
                mt: 0.5,
                fontSize: 13,
              }}
            >
              School Management System
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{
                mb: 3, borderRadius: '10px',
                animation: 'shake 0.4s ease-in-out',
                '@keyframes shake': {
                  '0%, 100%': { transform: 'translateX(0)' },
                  '25%': { transform: 'translateX(-5px)' },
                  '75%': { transform: 'translateX(5px)' },
                }
              }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" sx={{
              mb: 2, color: '#5a6a7e !important', fontWeight: 500, textAlign: 'center'
            }}>
              Sign in to your account
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                autoFocus
                autoComplete="email"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18, color: '#aaa' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px !important',
                    transition: 'all 0.3s ease',
                    '&:hover': { boxShadow: '0 2px 8px rgba(26,158,143,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 16px rgba(26,158,143,0.15)' },
                  }
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                margin="normal"
                autoComplete="current-password"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ fontSize: 18, color: '#aaa' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#999' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px !important',
                    transition: 'all 0.3s ease',
                    '&:hover': { boxShadow: '0 2px 8px rgba(26,158,143,0.1)' },
                    '&.Mui-focused': { boxShadow: '0 4px 16px rgba(26,158,143,0.15)' },
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.4,
                  background: 'linear-gradient(135deg, #1a9e8f 0%, #158a7d 100%) !important',
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: '10px !important',
                  textTransform: 'none',
                  letterSpacing: 0.3,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease !important',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #158a7d 0%, #0d7377 100%) !important',
                    boxShadow: '0 6px 20px rgba(26,158,143,0.4) !important',
                    transform: 'translateY(-2px) !important',
                  },
                  '&:active': {
                    transform: 'translateY(0) !important',
                  },
                  '&:disabled': {
                    background: '#ccc !important',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: '#fff !important' }} />
                    Signing in...
                  </Box>
                ) : 'Sign In'}
              </Button>
            </form>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/forgot-password')}
                sx={{
                  textTransform: 'none', color: '#1a9e8f', fontWeight: 600,
                  '&:hover': { background: 'rgba(26,158,143,0.06) !important' }
                }}
              >
                Forgot Password?
              </Button>
              <Typography variant="body2" sx={{ color: '#999 !important', fontSize: 12 }}>
                Need access? <strong style={{ color: '#1a9e8f' }}>Contact Admin</strong>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mt: 3,
            color: 'rgba(255,255,255,0.4) !important',
            fontSize: 12,
          }}
        >
          © {new Date().getFullYear()} Vidya Pratishthan's Supe English Medium School
        </Typography>
      </Container>
    </Box>
  );
};

export default Login;
